import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { PresenterWidgetsFacade } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.facade'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import {
  BaseWidget,
  CountdownWidget,
  HonourRollWidget,
  ItemTableCardType,
  ItemTableWidget,
  NotificationWidget,
  PresenterToolsEnum,
  ProjectorNotificationData,
  QrCodeWidgetModel,
  RaffleDrawWidgetModel,
  RaffleWinnersWidgetModel,
  SmartTextWidgetModel,
  TeamScoringWidget,
  TextWidgetModel,
  ThermometerWidgetModel,
  TotalRaisedWidget,
  WidgetModel,
  WidgetType,
  VideoWidgetModel,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterToolsItemTableComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-tools-item-table/gb-presenter-tools-item-table.component'
import { GbPresenterToolsTextWidgetComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-tools-text-widget/gb-presenter-tools-text-widget.component'
import { GbPresenterToolsSmartTextComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-tools-smart-text/gb-presenter-tools-smart-text.component'
import { combineLatest, distinctUntilChanged, map, Observable, Subscription } from 'rxjs'
import * as PresenterWidgetActions from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'
import { GbPresenterToolsCountdownComponent } from '../gb-presenter-tools-countdown/gb-presenter-tools-countdown.component'
import { GbPresenterToolsLayoutComponent } from '../gb-presenter-tools-layout/gb-presenter-tools-layout.component'
import { GbPresenterToolsRaffleDrawComponent } from '../gb-presenter-tools-raffle-draw/gb-presenter-tools-raffle-draw.component'
import { GbPresenterToolsRaffleWinnersListComponent } from '../gb-presenter-tools-raffle-winners-list/gb-presenter-tools-raffle-winners-list.component'
import { GbPresenterToolsTotalRaisedComponent } from '../gb-presenter-tools-total-raised/gb-presenter-tools-total-raised.component'
import { GbPresenterToolsHonourRollComponent } from '../gb-presenter-tools-honour-roll/gb-presenter-tools-honour-roll.component'
import { GbPresenterToolsThermometerComponent } from '../gb-presenter-tools-thermometer/gb-presenter-tools-thermometer.component'
import { GbPresenterToolsNotificationComponent } from '../gb-presenter-tools-notification/gb-presenter-tools-notification.component'
import { GbPresenterToolsQrCodeComponent } from '../gb-presenter-tools-qr-code/gb-presenter-tools-qr-code.component'
import { GbPresenterToolsTeamScoringComponent } from '../gb-presenter-tools-team-scoring/gb-presenter-tools-team-scoring.component'
import { GbPresenterToolsVideoComponent } from '../gb-presenter-tools-video/gb-presenter-tools-video.component'

@UntilDestroy()
@Component({
  selector: 'gb-presenter-tools',
  imports: [
    CommonModule,
    GbPresenterToolsNotificationComponent,
    GbPresenterToolsTextWidgetComponent,
    GbPresenterToolsSmartTextComponent,
    GbPresenterToolsItemTableComponent,
    GbPresenterToolsLayoutComponent,
    GbPresenterToolsTotalRaisedComponent,
    GbPresenterToolsCountdownComponent,
    GbPresenterToolsHonourRollComponent,
    GbPresenterToolsRaffleDrawComponent,
    GbPresenterToolsRaffleWinnersListComponent,
    GbPresenterToolsThermometerComponent,
    GbPresenterToolsQrCodeComponent,
    GbPresenterToolsTeamScoringComponent,
    GbPresenterToolsVideoComponent,
  ],
  templateUrl: './gb-presenter-tools.component.html',
  styleUrl: './gb-presenter-tools.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbPresenterToolsComponent implements OnInit {
  @Input() testData = false
  @Input() isTemplateMode = false
  @Output() previewWidget = new EventEmitter<WidgetType>()
  @Output() resetPreviewWidget = new EventEmitter<WidgetType>()
  @Output() previewNotification = new EventEmitter<ProjectorNotificationData>()
  @Output() alignLeft = new EventEmitter<void>()
  @Output() alignRight = new EventEmitter<void>()
  @Output() alignTop = new EventEmitter<void>()
  @Output() alignBottom = new EventEmitter<void>()
  @Output() alignHorizontalCenter = new EventEmitter<void>()
  @Output() alignVerticalCenter = new EventEmitter<void>()

  @Output() bringToFront = new EventEmitter<void>()
  @Output() bringForward = new EventEmitter<void>()
  @Output() sendBackward = new EventEmitter<void>()
  @Output() sendToBack = new EventEmitter<void>()

  private lastSelectedWidgetUuid: string | null = null
  private lastSelectedWidgetType: WidgetType | null = null

  PresenterToolsEnum = PresenterToolsEnum
  WidgetType = WidgetType
  widgetDefaultTools: PresenterToolsEnum[] = [PresenterToolsEnum.OPACITY, PresenterToolsEnum.LAYOUT]

  widgetToolsMap: ReadonlyMap<WidgetType, PresenterToolsEnum[]> = new Map([
    [WidgetType.TEXT, [...this.widgetDefaultTools, PresenterToolsEnum.TEXT]],
    [WidgetType.SMART_TEXT, [...this.widgetDefaultTools, PresenterToolsEnum.TEXT]],
    [WidgetType.IMAGE, [...this.widgetDefaultTools]],
    [WidgetType.ITEM_TABLE, [...this.widgetDefaultTools, PresenterToolsEnum.ITEM_TABLE]],
    [WidgetType.TOTAL_RAISED, [...this.widgetDefaultTools, PresenterToolsEnum.TOTAL_RAISED]],
    [WidgetType.COUNTDOWN, [...this.widgetDefaultTools, PresenterToolsEnum.COUNTDOWN]],
    [WidgetType.THERMOMETER, [...this.widgetDefaultTools, PresenterToolsEnum.THERMOMETER]],
    [WidgetType.HONOUR_ROLL, [...this.widgetDefaultTools, PresenterToolsEnum.HONOUR_ROLL]],
    [WidgetType.RAFFLE_DRAW, [...this.widgetDefaultTools, PresenterToolsEnum.RAFFLE_DRAW]],
    [WidgetType.RAFFLE_WINNERS, [...this.widgetDefaultTools, PresenterToolsEnum.RAFFLE_WINNERS]],
    [WidgetType.NOTIFICATION, [...this.widgetDefaultTools, PresenterToolsEnum.NOTIFICATION]],
    [WidgetType.QR_CODE, [...this.widgetDefaultTools, PresenterToolsEnum.QR_CODE]],
    [WidgetType.TEAM_SCORING, [...this.widgetDefaultTools, PresenterToolsEnum.TEAM_SCORING]],
    [WidgetType.VIDEO, [...this.widgetDefaultTools, PresenterToolsEnum.VIDEO]],
  ])

  toolsFormGroup = new FormGroup({})
  private autoSaveSubscription = new Subscription()

  tools$: Observable<PresenterToolsEnum[]> = this.presenterWidgetsFacade.selectedWidgetType$.pipe(
    map((selectedWidgetType) => {
      if (!selectedWidgetType) return []

      return this.widgetToolsMap.get(selectedWidgetType) || []
    }),
  )

  selectedWidgetType$ = this.presenterWidgetsFacade.selectedWidgetType$

  uuid$ = this.presenterWidgetsFacade.selectedWidget$.pipe(map((widget) => widget?.uuid))

  auctionScheduledEnd$ = this.selectedAuctionFacade.auction$.pipe(map((auction) => auction?.scheduled_end || null))
  auctionTimezone$ = this.selectedAuctionFacade.auction$.pipe(map((auction) => auction?.timezone))
  auctionOvertimeEnabled$ = this.selectedAuctionFacade.auction$.pipe(
    map((auction) => !!auction?.enable_scheduled_time_extend),
  )

  constructor(
    private presenterWidgetsFacade: PresenterWidgetsFacade,
    private selectedAuctionFacade: SelectedAuctionFacade,
  ) {}

  ngOnInit(): void {
    this.setupFormGroup()
  }

  onPreviewWidget(widgetType: WidgetType): void {
    this.previewWidget.emit(widgetType)
  }

  onResetPreviewWidget(widgetType: WidgetType): void {
    this.resetPreviewWidget.emit(widgetType)
  }

  private setupFormGroup(): void {
    combineLatest([this.presenterWidgetsFacade.selectedWidgetType$, this.presenterWidgetsFacade.selectedWidget$])
      .pipe(untilDestroyed(this))
      .subscribe(([widgetType, selectedWidget]) => {
        const nextUuid = selectedWidget?.uuid ?? null

        if (
          widgetType &&
          selectedWidget &&
          this.lastSelectedWidgetUuid === nextUuid &&
          this.lastSelectedWidgetType === widgetType &&
          this.toolsFormGroup.get('uuid')?.value === nextUuid
        ) {
          const xControl = this.toolsFormGroup.get('x') as FormControl<number> | null
          if (xControl) {
            xControl.setValue(selectedWidget.x ?? 0, { emitEvent: false })
          }

          const yControl = this.toolsFormGroup.get('y') as FormControl<number> | null
          if (yControl) {
            yControl.setValue(selectedWidget.y ?? 0, { emitEvent: false })
          }

          const widthControl = this.toolsFormGroup.get('width') as FormControl<number | null> | null
          if (widthControl) {
            widthControl.setValue(selectedWidget.width ?? null, { emitEvent: false })
          }

          const heightControl = this.toolsFormGroup.get('height') as FormControl<number | null> | null
          if (heightControl) {
            heightControl.setValue(selectedWidget.height ?? null, { emitEvent: false })
          }

          const zControl = this.toolsFormGroup.get('z') as FormControl<number> | null
          if (zControl) {
            zControl.setValue(selectedWidget.z ?? 0, { emitEvent: false })
          }
          return
        }

        this.lastSelectedWidgetUuid = nextUuid
        this.lastSelectedWidgetType = widgetType

        this.buildFormForWidgetType(widgetType, selectedWidget)

        if (selectedWidget) {
          this.trackAutoSaveFields(widgetType, selectedWidget.uuid)
        }
      })
  }

  private trackAutoSaveFields(widgetType: WidgetType | null, uuid: string): void {
    this.autoSaveSubscription.unsubscribe()
    this.autoSaveSubscription = new Subscription()

    if (!widgetType) return

    const fieldsToTrack = this.getAutoSaveFields(widgetType)
    if (fieldsToTrack.length === 0) return

    fieldsToTrack.forEach((field) => {
      const control = this.toolsFormGroup.get(field)
      if (control) {
        this.autoSaveSubscription.add(
          control.valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((value) => {
            this.dispatchUpdate(uuid, { [field]: value })
          }),
        )
      }
    })
  }

  private getAutoSaveFields(widgetType: WidgetType): string[] {
    switch (widgetType) {
      case WidgetType.TOTAL_RAISED:
        return [
          'track_bids',
          'track_donations',
          'track_purchases',
          'track_raffles',
          'track_ticket_sales',
          'base_total_raised_enabled',
          'hidden_items',
        ]
      case WidgetType.THERMOMETER:
        return ['track_pledges_only', 'base_total_raised_enabled', 'hidden_items']
      case WidgetType.HONOUR_ROLL:
        return ['hidden_items']
      default:
        return []
    }
  }

  private dispatchUpdate(uuid: string, update: Partial<WidgetModel>): void {
    this.presenterWidgetsFacade.dispatch(
      this.isTemplateMode
        ? PresenterWidgetActions.updateSelectedWidgetLocal({ widget: { ...update, uuid } })
        : PresenterWidgetActions.updateSelectedWidget({ widget: { ...update, uuid } }),
    )
  }

  private buildFormForWidgetType(widgetType: WidgetType | null, selectedWidget: WidgetModel | null): void {
    if (!widgetType || !selectedWidget) {
      this.toolsFormGroup = new FormGroup({})
      return
    }

    switch (widgetType) {
      case WidgetType.TEXT:
        this.toolsFormGroup = this.buildTextWidgetFormGroup(selectedWidget as TextWidgetModel)
        break
      case WidgetType.SMART_TEXT:
        this.toolsFormGroup = this.buildSmartTextWidgetFormGroup(selectedWidget as SmartTextWidgetModel)
        break
      case WidgetType.ITEM_TABLE:
        this.toolsFormGroup = this.buildItemTableWidgetFormGroup(selectedWidget as ItemTableWidget)
        break
      case WidgetType.TOTAL_RAISED:
        this.toolsFormGroup = this.buildTotalRaisedWidgetFormGroup(selectedWidget as TotalRaisedWidget)
        break
      case WidgetType.COUNTDOWN:
        this.toolsFormGroup = this.buildCountdownWidgetFormGroup(selectedWidget as CountdownWidget)
        break
      case WidgetType.HONOUR_ROLL:
        this.toolsFormGroup = this.buildHonourRollWidgetFormGroup(selectedWidget as HonourRollWidget)
        break
      case WidgetType.RAFFLE_DRAW:
        this.toolsFormGroup = this.buildRaffleDrawWidgetFormGroup(selectedWidget as RaffleDrawWidgetModel)
        break
      case WidgetType.RAFFLE_WINNERS:
        this.toolsFormGroup = this.buildRaffleWinnersWidgetFormGroup(selectedWidget as RaffleWinnersWidgetModel)
        break
      case WidgetType.THERMOMETER:
        this.toolsFormGroup = this.buildThermometerWidgetFormGroup(selectedWidget as ThermometerWidgetModel)
        break
      case WidgetType.NOTIFICATION:
        this.toolsFormGroup = this.buildNotificataionWidgetFormGroup(selectedWidget as NotificationWidget)
        break
      case WidgetType.QR_CODE:
        this.toolsFormGroup = this.buildQrCodeWidgetFormGroup(selectedWidget as QrCodeWidgetModel)
        break
      case WidgetType.TEAM_SCORING:
        this.toolsFormGroup = this.buildTeamScoringWidgetFormGroup(selectedWidget as TeamScoringWidget)
        break
      case WidgetType.VIDEO:
        this.toolsFormGroup = this.buildVideoWidgetFormGroup(selectedWidget as VideoWidgetModel)
        break
      default:
        this.toolsFormGroup = this.buildBaseWidgetFormGroup(selectedWidget as BaseWidget)
        break
    }
  }

  private buildVideoWidgetFormGroup(videoWidget: VideoWidgetModel): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(videoWidget)
    formGroup.addControl('video_url', new FormControl(videoWidget.video_url ?? ''))
    return formGroup
  }

  private buildThermometerWidgetFormGroup(thermometerWidget: ThermometerWidgetModel): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(thermometerWidget)

    formGroup.addControl('fundraising_target', new FormControl(thermometerWidget.fundraising_target ?? 1000))

    formGroup.addControl('track_pledges_only', new FormControl(thermometerWidget.track_pledges_only ?? false))
    formGroup.addControl(
      'base_total_raised_enabled',
      new FormControl(thermometerWidget.base_total_raised_enabled ?? false),
    )

    formGroup.addControl(
      'thermometer_background_color',
      new FormControl(thermometerWidget.thermometer_background_color ?? '#ffffff'),
    )
    formGroup.addControl(
      'thermometer_border_color',
      new FormControl(thermometerWidget.thermometer_border_color ?? '#263238'),
    )
    formGroup.addControl('thermometer_color', new FormControl(thermometerWidget.thermometer_color ?? '#F44336'))

    formGroup.addControl('custom_image', new FormControl(thermometerWidget.custom_image ?? null))
    formGroup.addControl('hidden_items', new FormControl(thermometerWidget.hidden_items ?? []))

    return formGroup
  }

  private buildBaseWidgetFormGroup(widget: BaseWidget): FormGroup {
    return new FormGroup({
      uuid: new FormControl(widget.uuid),
      x: new FormControl(widget.x ?? 0),
      x_unit: new FormControl(widget.x_unit ?? '%'),
      y: new FormControl(widget.y ?? 0),
      y_unit: new FormControl(widget.y_unit ?? '%'),
      z: new FormControl(widget.z ?? 0),
      rotate_z: new FormControl(widget.rotate_z ?? 0),
      rotate_z_unit: new FormControl(widget.rotate_z_unit ?? 'deg'),
      width: new FormControl(widget.width),
      height: new FormControl(widget.height),
      background_color: new FormControl(widget.background_color ?? ''),
      opacity: new FormControl(widget.opacity ?? 1),
      border_color: new FormControl(widget.border_color ?? ''),
      border_width: new FormControl(widget.border_width),
      border_width_unit: new FormControl(widget.border_width_unit ?? 'px'),
      border_style: new FormControl(widget.border_style ?? ''),
      border_radius: new FormControl(widget.border_radius),
      border_radius_unit: new FormControl(widget.border_radius_unit ?? 'px'),
      padding: new FormControl(widget.padding),
      padding_unit: new FormControl(widget.padding_unit ?? 'px'),
      box_shadow: new FormControl(widget.box_shadow ?? ''),
      font_family: new FormControl(widget.font_family ?? ''),
      font_weight: new FormControl(widget.font_weight ?? ''),
      font_size: new FormControl(widget.font_size ?? 5),
      color: new FormControl(widget.color ?? ''),
      text_align: new FormControl(widget.text_align ?? ''),
    })
  }

  private buildTextWidgetFormGroup(textWidget: TextWidgetModel): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(textWidget)
    formGroup.addControl('text', new FormControl(textWidget.text || ''))
    formGroup.addControl('font_style', new FormControl(textWidget.font_style || 'normal'))
    formGroup.addControl('text_decoration', new FormControl(textWidget.text_decoration || 'none'))
    formGroup.addControl('line_height', new FormControl(textWidget.line_height ?? 1))
    formGroup.addControl('letter_spacing', new FormControl(textWidget.letter_spacing ?? 0))
    return formGroup
  }

  private buildSmartTextWidgetFormGroup(smartTextWidget: SmartTextWidgetModel): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(smartTextWidget)
    formGroup.addControl('text', new FormControl(smartTextWidget.text || ''))
    return formGroup
  }

  private buildItemTableWidgetFormGroup(itemTableWidget: ItemTableWidget): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(itemTableWidget)
    formGroup.addControl('columns', new FormControl(itemTableWidget.columns ?? 1))
    formGroup.addControl('rows', new FormControl(itemTableWidget.rows ?? 5))
    formGroup.addControl('slide_interval', new FormControl(itemTableWidget.slide_interval ?? 10))
    formGroup.addControl('table_primary_color', new FormControl(itemTableWidget.table_primary_color ?? '#000000'))
    formGroup.addControl('table_accent_color', new FormControl(itemTableWidget.table_accent_color ?? '#ffd166'))
    formGroup.addControl('table_background_color', new FormControl(itemTableWidget.table_background_color ?? '#ffffff'))
    formGroup.addControl('card_type', new FormControl(itemTableWidget.card_type ?? ItemTableCardType.STANDARD_TABLE))
    formGroup.addControl('hidden_items', new FormControl(itemTableWidget.hidden_items ?? []))
    formGroup.addControl('transition', new FormControl(itemTableWidget.transition ?? 'fade-in'))
    formGroup.addControl('image_contained', new FormControl(itemTableWidget.image_contained ?? true))
    formGroup.addControl('show_item_type', new FormControl(itemTableWidget.show_item_type ?? true))
    formGroup.addControl('item_name_max_lines', new FormControl(itemTableWidget.item_name_max_lines ?? null))
    return formGroup
  }

  private buildTotalRaisedWidgetFormGroup(totalRaisedWidget: TotalRaisedWidget): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(totalRaisedWidget)

    formGroup.addControl('label', new FormControl(totalRaisedWidget.label ?? ''))

    formGroup.addControl('track_bids', new FormControl(totalRaisedWidget.track_bids ?? true))
    formGroup.addControl('track_donations', new FormControl(totalRaisedWidget.track_donations ?? true))
    formGroup.addControl('track_purchases', new FormControl(totalRaisedWidget.track_purchases ?? true))
    formGroup.addControl('track_raffles', new FormControl(totalRaisedWidget.track_raffles ?? true))
    formGroup.addControl('track_ticket_sales', new FormControl(totalRaisedWidget.track_ticket_sales ?? true))
    formGroup.addControl(
      'base_total_raised_enabled',
      new FormControl(totalRaisedWidget.base_total_raised_enabled ?? false),
    )
    formGroup.addControl('hidden_items', new FormControl(totalRaisedWidget.hidden_items ?? []))

    return formGroup
  }

  private buildCountdownWidgetFormGroup(countdownWidget: CountdownWidget): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(countdownWidget)

    formGroup.addControl('end', new FormControl(countdownWidget.end ?? ''))
    formGroup.addControl('timeout_text', new FormControl(countdownWidget.timeout_text ?? 'Countdown finished'))
    formGroup.addControl('timer_type', new FormControl(countdownWidget.timer_type ?? ''))
    formGroup.addControl('plus_overtime_label', new FormControl(countdownWidget.plus_overtime_label ?? true))
    formGroup.addControl(
      'end_on_auction_schedule_close',
      new FormControl(countdownWidget.end_on_auction_schedule_close ?? false),
    )

    formGroup.addControl('letter_spacing', new FormControl(countdownWidget.letter_spacing ?? 1))

    return formGroup
  }

  private buildHonourRollWidgetFormGroup(honourRollWidget: HonourRollWidget): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(honourRollWidget)

    formGroup.addControl('latest_activities', new FormControl(honourRollWidget.latest_activities ?? 10))
    formGroup.addControl('transition', new FormControl(honourRollWidget.transition ?? 'scroll-right'))
    formGroup.addControl('rows', new FormControl(honourRollWidget.rows ?? null))
    formGroup.addControl('columns', new FormControl(honourRollWidget.columns ?? null))
    formGroup.addControl('layout_direction', new FormControl(honourRollWidget.layout_direction ?? null))
    formGroup.addControl('slide_interval', new FormControl(honourRollWidget.slide_interval ?? 10.0))

    formGroup.addControl('table_background_color', new FormControl(honourRollWidget.table_background_color))
    formGroup.addControl('table_primary_color', new FormControl(honourRollWidget.table_primary_color ?? '#39a065'))
    formGroup.addControl('table_accent_color', new FormControl(honourRollWidget.table_accent_color ?? '#39a065'))
    formGroup.addControl('show_item_name', new FormControl(honourRollWidget.show_item_name ?? true))
    formGroup.addControl('show_item_alias', new FormControl(honourRollWidget.show_item_alias ?? true))

    formGroup.addControl('hidden_items', new FormControl(honourRollWidget.hidden_items ?? []))

    return formGroup
  }

  private buildRaffleDrawWidgetFormGroup(raffleDrawWidget: RaffleDrawWidgetModel): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(raffleDrawWidget)

    formGroup.addControl('raffle_item', new FormControl(raffleDrawWidget.raffle_item ?? null, Validators.required))
    formGroup.addControl('primary_color', new FormControl(raffleDrawWidget.primary_color ?? '#ffffff'))
    formGroup.addControl('primary_text_color', new FormControl(raffleDrawWidget.primary_text_color ?? '#000000'))

    formGroup.addControl('enable_confetti', new FormControl(raffleDrawWidget.enable_confetti ?? false))
    formGroup.addControl('enable_bulk_raffle_draw', new FormControl(raffleDrawWidget.enable_bulk_raffle_draw ?? false))
    formGroup.addControl('show_prize_numbers', new FormControl(raffleDrawWidget.show_prize_numbers ?? false))

    return formGroup
  }

  private buildRaffleWinnersWidgetFormGroup(raffleWinnersWidget: RaffleWinnersWidgetModel): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(raffleWinnersWidget)

    formGroup.addControl('raffle_item', new FormControl(raffleWinnersWidget.raffle_item ?? null, Validators.required))

    formGroup.addControl('winners', new FormControl([]))

    formGroup.addControl('rows', new FormControl(raffleWinnersWidget.rows ?? 5))
    formGroup.addControl('columns', new FormControl(raffleWinnersWidget.columns ?? 1))

    formGroup.addControl('primary_color', new FormControl(raffleWinnersWidget.primary_color ?? '#ffffff'))
    formGroup.addControl('primary_text_color', new FormControl(raffleWinnersWidget.primary_text_color ?? '#000000'))
    formGroup.addControl('accent_color', new FormControl(raffleWinnersWidget.accent_color ?? '#ffd166'))
    formGroup.addControl('accent_text_color', new FormControl(raffleWinnersWidget.font_color ?? '#000000'))

    return formGroup
  }

  private buildNotificataionWidgetFormGroup(NotificationWidget: NotificationWidget): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(NotificationWidget)

    formGroup.addControl(
      'notif_secondary_color',
      new FormControl(NotificationWidget.notif_secondary_color ?? 'rgba(255, 255, 255, 0.87)'),
    )
    formGroup.addControl(
      'notif_secondary_background_color',
      new FormControl(NotificationWidget.notif_secondary_background_color ?? 'rgba(57, 160, 128, 1)'),
    )
    formGroup.addControl('notif_type', new FormControl(NotificationWidget.notif_type ?? 'popout_a'))

    formGroup.addControl('show_bid_notifications', new FormControl(NotificationWidget.show_bid_notifications ?? true))
    formGroup.addControl(
      'show_purchase_notifications',
      new FormControl(NotificationWidget.show_purchase_notifications ?? true),
    )
    formGroup.addControl(
      'show_donation_notifications',
      new FormControl(NotificationWidget.show_donation_notifications ?? true),
    )
    formGroup.addControl(
      'show_custom_notifications',
      new FormControl(NotificationWidget.show_custom_notifications ?? true),
    )

    formGroup.addControl('hide_name', new FormControl(NotificationWidget.hide_name ?? false))
    formGroup.addControl('hide_price', new FormControl(NotificationWidget.hide_price ?? false))
    formGroup.addControl('image_contained', new FormControl(NotificationWidget.image_contained ?? true))

    return formGroup
  }

  private buildQrCodeWidgetFormGroup(qrCodeWidget: QrCodeWidgetModel): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(qrCodeWidget)

    formGroup.addControl('url', new FormControl(qrCodeWidget.url ?? '', [Validators.required]))
    formGroup.addControl('hide_url', new FormControl(qrCodeWidget.hide_url ?? false))
    return formGroup
  }

  private buildTeamScoringWidgetFormGroup(teamScoringWidget: TeamScoringWidget): FormGroup {
    const formGroup = this.buildBaseWidgetFormGroup(teamScoringWidget)

    formGroup.addControl('columns', new FormControl(teamScoringWidget.columns ?? 1))
    formGroup.addControl('rows', new FormControl(teamScoringWidget.rows ?? 5))
    formGroup.addControl('slide_interval', new FormControl(teamScoringWidget.slide_interval ?? 30))
    formGroup.addControl('table_primary_color', new FormControl(teamScoringWidget.table_primary_color ?? '#000000'))
    formGroup.addControl('table_accent_color', new FormControl(teamScoringWidget.table_accent_color ?? '#ffd166'))
    formGroup.addControl(
      'table_background_color',
      new FormControl(teamScoringWidget.table_background_color ?? '#ffffff'),
    )
    formGroup.addControl('primary_text_color', new FormControl(teamScoringWidget.primary_text_color ?? '#000000'))
    formGroup.addControl('card_type', new FormControl(teamScoringWidget.card_type ?? 'standard_table'))
    formGroup.addControl('hidden_teams', new FormControl(teamScoringWidget.hidden_teams ?? []))
    formGroup.addControl('transition', new FormControl(teamScoringWidget.transition ?? 'fade-in'))

    return formGroup
  }
}
